import React, {useEffect} from 'react';
import {
  Button,
  ButtonBase,
  Backdrop,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Box,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import moment from 'moment-timezone';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import TodayIcon from '@material-ui/icons/Today';
import FlashOn from '@material-ui/icons/FlashOn';
import ReactGA from 'react-ga4';
import {DateRangePicker} from 'materialui-daterange-picker';
import useStore from '../../store/store';
import usePersistentStore from '../../store/persistentStore';
import {buildComparisonPeriod, normalizeComparisonMode} from '../helpers/comparisonPeriod';

/** Earliest prebid analytics date with exchange-rate data in our database. */
const ANALYTICS_EARLIEST_DATE = '2025-11-25';

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'inline-block',
    margin: theme.spacing(0.5),
    borderRadius: 4,
    background: 'none',
  },
  mobilePeriodPickerPaper: {
    width: '100%',
  },
  button: {
    margin: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: '90px',
  },
  datePickerOutlined: {
    padding: '4px 16px',
  },
  datePickerRoot: {
    fontSize: '0.8125rem',
    lineHeight: 1.75,
    textTransform: 'uppercase',
  },
  datePickerRootActive: {
    background: theme.palette.primary.main,
    color: 'white',
    fontSize: '0.8125rem',
    lineHeight: 1.75,
    textTransform: 'uppercase',
  },
  realtimeIcon: {
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

export function PeriodPicker({isMobile, stackedReportType, autoRefreshLoading = false}) {
  const classes = useStyles();

  const [rangePickerOpen, setRangePickerOpen] = React.useState(false);
  const [mobilePeriodPicker, setMobilePeriodPicker] = React.useState(null);

  const setState = useStore(state => state.setState);
  const period = useStore(state => state.period);
  const startingDay = useStore(state => state.startingDay);
  const calculated = useStore(state => state.calculated);

  const isAnalytics = stackedReportType === 'analytics';
  const analyticsMinDate = isAnalytics ? moment(ANALYTICS_EARLIEST_DATE) : undefined;
  const realtimeMinutes = usePersistentStore(state => state.realtimeMinutes);
  const comparisonMode = usePersistentStore(state => state.comparisonMode);

  const applyComparisonPeriod = (startDate, endDate) => {
    setState(
      'comparisonPeriod',
      buildComparisonPeriod(startDate, endDate, normalizeComparisonMode(comparisonMode))
    );
  };

  // If on analytics with intra-day periods and switch to another report, restore last 30 days once
  useEffect(() => {
    if (
      !isAnalytics &&
      (period.label === 'Last 24 hours' || period.label === 'realtime' || period.label === 'Today')
    ) {
      const startDate = moment(startingDay).subtract(30, 'days').format('YYYY-MM-DD');
      const endDate = moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD');

      setState('period', {label: 'Last 30 days', startDate, endDate});
      applyComparisonPeriod(startDate, endDate);
    }
  }, [isAnalytics, period.label, setState, startingDay, comparisonMode]);

  const changePeriod = range => {
    ReactGA.event({
      category: 'period picker',
      action: 'click',
      label: range.label ? range.label : 'custom',
    });

    setRangePickerOpen(false);

    setState('period', {
      startDate: moment(range.startDate).format('YYYY-MM-DD'),
      endDate: moment(range.endDate).format('YYYY-MM-DD'),
      label: range.label,
      ...(range.timezone ? {timezone: range.timezone} : {}),
    });

    setState('calculated', null);

    const startDate = moment(range.startDate).format('YYYY-MM-DD');
    const endDate = moment(range.endDate).format('YYYY-MM-DD');
    applyComparisonPeriod(startDate, endDate);
  };

  const isYesterdayDisabled =
    parseInt(moment.tz('Europe/Vilnius').format('HH')) < 7 && !isAnalytics;

  const selectToday = () => {
    const timezone = moment.tz.guess();
    const today = moment.tz(timezone).format('YYYY-MM-DD');
    changePeriod({
      label: 'Today',
      startDate: today,
      endDate: today,
      timezone,
    });
  };

  const thisMonthPeriod = {
    label: 'This month',
    startDate: moment(startingDay).startOf('month').format('YYYY-MM-DD'),
    endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
  };

  const allHistoryPeriod = {
    label: 'All history',
    startDate: isAnalytics
      ? ANALYTICS_EARLIEST_DATE
      : moment(startingDay).subtract(10, 'year').format('YYYY-MM-DD'),
    endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
  };

  const definedRanges = [
    {
      label: 'Last week',
      startDate: moment().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
      endDate: moment().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 7 days',
      startDate: moment(startingDay).subtract(7, 'days').format('YYYY-MM-DD'),
      endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Last 30 days',
      startDate: moment(startingDay).subtract(31, 'days').format('YYYY-MM-DD'),
      endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      label: 'Last month',
      startDate: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
      endDate: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
    },
    {
      label: 'This year',
      startDate: moment(startingDay).startOf('year').format('YYYY-MM-DD'),
      endDate: moment(startingDay).subtract(1, 'day').endOf('month').format('YYYY-MM-DD'),
    },
    {
      label: 'Last year',
      startDate: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      endDate: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
    },
    allHistoryPeriod,
    thisMonthPeriod,
  ].filter(range => {
    if (!analyticsMinDate) return true;
    return moment(range.startDate).isSameOrAfter(analyticsMinDate, 'day');
  });

  return (
    <>
      {isMobile && (
        <>
          <Button
            variant={'outlined'}
            fullWidth
            onClick={e => setMobilePeriodPicker(e.currentTarget)}
            endIcon={<CalendarTodayIcon />}
          >
            {period.label && isMobile
              ? period.label
              : period.label
                ? `Custom period`
                : `${period.startDate} - ${period.endDate}`}
          </Button>
          <Backdrop
            className={classes.backdrop}
            open={false}
            onClick={() => setMobilePeriodPicker(null)}
          >
            <Menu
              anchorEl={mobilePeriodPicker}
              keepMounted
              open={Boolean(mobilePeriodPicker)}
              onClose={() => setMobilePeriodPicker(null)}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              classes={{paper: classes.mobilePeriodPickerPaper}}
            >
              {isAnalytics && period.label !== 'Last 24 hours' && (
                <MenuItem
                  onClick={() => {
                    changePeriod({
                      label: 'Last 24 hours',
                      startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                      endDate: moment().format('YYYY-MM-DD'),
                    });
                    setMobilePeriodPicker(null);
                  }}
                >
                  <AccessTimeIcon fontSize="small" style={{marginRight: 8}} />
                  Last 24 hours
                </MenuItem>
              )}
              {isAnalytics && period.label !== 'Today' && (
                <MenuItem
                  onClick={() => {
                    selectToday();
                    setMobilePeriodPicker(null);
                  }}
                >
                  <TodayIcon fontSize="small" style={{marginRight: 8}} />
                  Today
                </MenuItem>
              )}
              {period.label !== 'Yesterday' && (
                <Tooltip
                  title={
                    isYesterdayDisabled ? 'Yesterdays data will be available after 7AM EEST' : ''
                  }
                >
                  <MenuItem
                    disabled={isYesterdayDisabled}
                    onClick={() => {
                      changePeriod({
                        label: 'Yesterday',
                        startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                        endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                      });
                      setMobilePeriodPicker(null);
                    }}
                  >
                    Yesterday {isYesterdayDisabled ? '(will be available after 7AM EEST)' : ''}
                  </MenuItem>
                </Tooltip>
              )}
              {period.label !== 'Last 7 days' && (
                <MenuItem
                  onClick={() => {
                    changePeriod({
                      label: 'Last 7 days',
                      startDate: moment(startingDay).subtract(7, 'days').format('YYYY-MM-DD'),
                      endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
                    });
                    setMobilePeriodPicker(null);
                  }}
                >
                  Last 7 days
                </MenuItem>
              )}
              {period.label !== 'Last 30 days' && (
                <MenuItem
                  onClick={() => {
                    changePeriod({
                      label: 'Last 30 days',
                      startDate: moment(startingDay).subtract(30, 'days').format('YYYY-MM-DD'),
                      endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
                    });
                    setMobilePeriodPicker(null);
                  }}
                >
                  Last 30 days
                </MenuItem>
              )}
              {period.label !== 'Last month' && (
                <MenuItem
                  onClick={() => {
                    changePeriod({
                      label: 'Last month',
                      startDate: moment()
                        .subtract(1, 'months')
                        .startOf('month')
                        .format('YYYY-MM-DD'),
                      endDate: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
                    });
                    setMobilePeriodPicker(null);
                  }}
                >
                  Last month
                </MenuItem>
              )}
              {period.label !== 'All history' && (
                <MenuItem
                  onClick={() => {
                    changePeriod(allHistoryPeriod);
                    setMobilePeriodPicker(null);
                  }}
                >
                  All history
                </MenuItem>
              )}
              {period.label !== 'This month' && (
                <MenuItem
                  onClick={() => {
                    changePeriod(thisMonthPeriod);
                    setMobilePeriodPicker(null);
                  }}
                >
                  This month
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setRangePickerOpen(true);
                  setMobilePeriodPicker(null);
                }}
              >
                Custom period
              </MenuItem>
            </Menu>
          </Backdrop>
        </>
      )}

      {!isMobile && (
        <>
          {isAnalytics && (
            <>
              <Button
                className={classes.button}
                variant={period.label === 'realtime' ? 'contained' : 'outlined'}
                color={period.label === 'realtime' ? 'primary' : 'default'}
                size="small"
                startIcon={
                  <Box className={classes.realtimeIcon}>
                    {autoRefreshLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <FlashOn fontSize="small" />
                    )}
                  </Box>
                }
                onClick={() => {
                  changePeriod({
                    label: 'realtime',
                    startDate: moment().format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD'),
                    realtimeMinutes: realtimeMinutes,
                  });
                }}
              >
                Realtime
              </Button>
              <Button
                className={classes.button}
                variant={period.label === 'Last 24 hours' ? 'contained' : 'outlined'}
                color={period.label === 'Last 24 hours' ? 'primary' : 'default'}
                size="small"
                onClick={() => {
                  changePeriod({
                    label: 'Last 24 hours',
                    startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD'),
                  });
                }}
              >
                Last 24 hours
              </Button>
              <Button
                className={classes.button}
                variant={period.label === 'Today' ? 'contained' : 'outlined'}
                color={period.label === 'Today' ? 'primary' : 'default'}
                size="small"
                onClick={selectToday}
              >
                Today
              </Button>
            </>
          )}

          <Tooltip
            title={isYesterdayDisabled ? 'Yesterdays data will be available after 7AM EEST' : ''}
          >
            <div style={{display: 'inline-block'}}>
              <Button
                disabled={isYesterdayDisabled}
                className={classes.button}
                variant={period.label === 'Yesterday' ? 'contained' : 'outlined'}
                color={period.label === 'Yesterday' ? 'primary' : 'default'}
                size="small"
                onClick={() => {
                  changePeriod({
                    label: 'Yesterday',
                    startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                    endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                  });
                }}
              >
                Yesterday
              </Button>
            </div>
          </Tooltip>

          <Button
            //disabled={!calculated}
            className={classes.button}
            variant={period.label === 'Last 30 days' ? 'contained' : 'outlined'}
            color={period.label === 'Last 30 days' ? 'primary' : 'default'}
            size="small"
            onClick={() => {
              changePeriod({
                label: 'Last 30 days',
                startDate: moment(startingDay).subtract(30, 'days').format('YYYY-MM-DD'),
                endDate: moment(startingDay).subtract(1, 'day').format('YYYY-MM-DD'),
              });
            }}
          >
            Last 30 days
          </Button>

          <Button
            //disabled={!calculated}
            className={classes.button}
            variant={period.label === 'Last month' ? 'contained' : 'outlined'}
            color={period.label === 'Last month' ? 'primary' : 'default'}
            size="small"
            onClick={() => {
              changePeriod({
                label: 'Last month',
                startDate: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
                endDate: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
              });
            }}
          >
            Last month
          </Button>

          <Button
            className={classes.button}
            variant={period.label === 'This month' ? 'contained' : 'outlined'}
            color={period.label === 'This month' ? 'primary' : 'default'}
            size="small"
            onClick={() => changePeriod(thisMonthPeriod)}
          >
            This month
          </Button>
        </>
      )}

      <ButtonBase>
        <Select
          classes={{
            root: !period.label ? classes.datePickerRootActive : classes.datePickerRoot,
            outlined: classes.datePickerOutlined,
          }}
          style={{left: isMobile ? 0 : 8, display: isMobile ? 'none' : 'flex'}}
          variant={isMobile ? 'filled' : 'outlined'}
          IconComponent={isMobile ? CalendarTodayIcon : undefined}
          color={'primary'}
          renderValue={() =>
            period.label && isMobile
              ? period.label
              : period.label
                ? `Custom period`
                : `${period.startDate} - ${period.endDate}`
          }
          displayEmpty
          open={rangePickerOpen}
          ////disabled={!calculated}
          onOpen={() => setRangePickerOpen(!rangePickerOpen)}
          MenuProps={{
            MenuListProps: {
              disablePadding: true,
              dense: true,
            },
          }}
        >
          <DateRangePicker
            initialDateRange={period}
            open={true}
            // If it's after 7AM in Europe/Vilnius (EEST), then last available day is yesterday, else it's day before yesterday
            maxDate={
              isYesterdayDisabled ? moment().subtract(2, 'days') : moment().subtract(1, 'day')
            }
            minDate={analyticsMinDate}
            definedRanges={!isMobile ? definedRanges : []}
            toggle={() => setRangePickerOpen(!rangePickerOpen)}
            onChange={range => {
              changePeriod(range);
            }}
          />
        </Select>
      </ButtonBase>
    </>
  );
}
